<template>
  <el-form
    ref="custom-data"
    :model="attributes"
    class="profile-tab"
    :key="key"
    :style="{ height: height, overflow: 'auto', 'padding-bottom': '50px' }"
    data-qa="preview-custom-data-tab"
  >
    <div
      v-if="regularAttributesCount > 0"
      class="attributes-section"
      data-qa="preview-custom-data-attributes"
    >
      <div class="section-header">
        {{ $t('CampaignForm_Preview_Attributes') }}
      </div>
      <form-item
        class="flexbox"
        v-for="([attrName], index) in regularAttributesData"
        :label="attrName"
        :key="`attr-${attrName}`"
        :prop="'data.' + index + '.1'"
        :rules="getRule(attrName)"
        :data-qa="`preview-custom-data-attribute-${attrName}`"
      >
        <el-tooltip
          :content="getUnmetTooltipMessage(attrName)"
          placement="top"
          effect="light"
          :disabled="!hasUnmetAttributeConditions(attrName)"
        >
          <el-date-picker
            :class="[
              'borderless-datepicker',
              getUnmetConditionsClass(attrName)
            ]"
            v-if="getAttrType(attrName) === 'datetime'"
            :value="customAtts[attrName]"
            type="datetime"
            format="yyyy-MM-dd HH:mm:ss"
            value-format="yyyy-MM-dd HH:mm:ss"
            @input="onAttrInput(attrName, $event)"
            :clearable="false"
            :data-qa="`preview-custom-data-attribute-input-${attrName}`"
          />
          <el-date-picker
            :class="[
              'borderless-datepicker',
              getUnmetConditionsClass(attrName)
            ]"
            v-else-if="['date'].includes(getAttrType(attrName))"
            :value="customAtts[attrName]"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            @input="onAttrInput(attrName, $event)"
            :clearable="false"
            :data-qa="`preview-custom-data-attribute-input-${attrName}`"
          />

          <el-input
            v-else
            :class="['borderless', getUnmetConditionsClass(attrName)]"
            :value="customAtts[attrName]"
            @input="onAttrInput(attrName, $event, getRule(attrName))"
            :data-qa="`preview-custom-data-attribute-input-${attrName}`"
          />
        </el-tooltip>
      </form-item>
    </div>

    <div
      v-if="subscriptionPreferencesEnabled && consentPreferencesCount > 0"
      class="preferences-section"
      data-qa="preview-custom-data-preferences"
    >
      <div class="section-header">
        {{ $t('CampaignForm_Preview_Preferences') }}
      </div>
      <form-item
        class="flexbox"
        v-for="(val, attr) in consentPreferences"
        :label="getConsentPreferenceLabel(attr)"
        :key="attr"
        :data-qa="`preview-custom-data-preference-${attr}`"
      >
        <el-tooltip
          :content="getUnmetTooltipMessage(attr)"
          placement="top"
          effect="light"
          :disabled="!hasUnmetAttributeConditions(attr)"
        >
          <el-select
            class="preference-value"
            :class="getUnmetConditionsClass(attr)"
            :value="getConsentPreferenceValueForSelect(attr)"
            @change="onPreferenceChange(attr, $event)"
            placeholder="-"
            :data-qa="`preview-custom-data-preference-select-${attr}`"
          >
            <el-option
              v-for="option in preferenceOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
        </el-tooltip>
      </form-item>
    </div>
  </el-form>
</template>

<script>
import { EventBus } from '@/core/EventBus'
import _ from 'lodash'
import FormItem from '@/common/components/form/FormItem.vue'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import SubscriptionPreferencesMixin from './mixins/SubscriptionPreferencesMixin'

export default {
  name: 'PreviewContentCustomDataTab',
  components: { FormItem },
  mixins: [SubscriptionPreferencesMixin],
  data() {
    return {
      height: '450px',
      attributes: {},
      key: null,
      rules: {},
      timeoutId: null,
      customAtts: {},
      resetPending: true,
      data: [],
      loading: false,
      attrTypes: {},
      unmetConditions: [],
      profileId: null,
      preferenceOptions: [
        { label: '0', value: '0' },
        { label: '1', value: '1' },
        { label: 'NULL', value: 'NULL' }
      ]
    }
  },
  mounted() {
    this.calculateHeight()
    this.fetchCustomChannels()
    this.onSetProfileAttributes = attributes => {
      const incoming = attributes || {}
      if (!this.resetPending && this.hasSameAttributeKeys(incoming)) {
        return
      }
      this.customAtts = this.reconcileAttributes(incoming, this.resetPending)
      this.resetPending = false
      this.setAttributeRules()
      this.attributes = { data: Object.entries(this.customAtts) }
      this.checkAndFetchPreferences()
    }
    this.onSetUnmetConditions = unmetConditions => {
      this.unmetConditions = unmetConditions
    }
    this.onSetProfileId = profileId => {
      this.profileId = profileId
      this.checkAndFetchPreferences()
    }
    this.onResetCustomData = () => {
      this.resetPending = true
    }
    EventBus.$on('set-profile-attributes', this.onSetProfileAttributes)
    EventBus.$on('set-unmet-attribute-conditions', this.onSetUnmetConditions)
    EventBus.$on('set-profile-id', this.onSetProfileId)
    EventBus.$on('reset-custom-data', this.onResetCustomData)
  },
  computed: {
    regularAttributes() {
      if (!this.customAtts || typeof this.customAtts !== 'object') {
        return {}
      }
      return Object.keys(this.customAtts).reduce((regular, attr) => {
        if (!this.isConsentPreference(attr)) {
          regular[attr] = this.customAtts[attr]
        }
        return regular
      }, {})
    },
    regularAttributesData() {
      return Object.entries(this.regularAttributes)
    },
    regularAttributesCount() {
      return Object.keys(this.regularAttributes).length
    }
  },
  methods: {
    getAttributes() {
      return this.customAtts
    },
    /**
     * Own-property lookups for the rules/attrTypes maps. Custom attribute names
     * are user-defined and may collide with Object.prototype members (e.g.
     * `toString`, `hasOwnProperty`); a bare `rules[name]` would then fall
     * through to the inherited function and pass a Function where the template
     * expects a rule object / type string.
     */
    getRule(name) {
      return Object.prototype.hasOwnProperty.call(this.rules, name)
        ? this.rules[name]
        : null
    },
    getAttrType(name) {
      return Object.prototype.hasOwnProperty.call(this.attrTypes, name)
        ? this.attrTypes[name]
        : null
    },
    reconcileAttributes(incoming, isReset) {
      if (isReset) {
        return _.cloneDeep(incoming)
      }

      const reconciled = {}
      Object.keys(incoming).forEach(key => {
        reconciled[key] = Object.prototype.hasOwnProperty.call(
          this.customAtts,
          key
        )
          ? this.customAtts[key]
          : incoming[key]
      })
      return reconciled
    },
    hasSameAttributeKeys(incoming) {
      return _.isEqual(
        Object.keys(this.customAtts).sort(),
        Object.keys(incoming).sort()
      )
    },
    syncPreferenceToAttributes(channel, name, status) {
      Object.keys(this.consentPreferences || {}).forEach(attr => {
        const parsed = this.parseConsentPreferenceKey(attr)
        if (parsed && parsed.channel === channel && parsed.name === name) {
          this.$set(this.customAtts, attr, status)
        }
      })
    },
    onPreferencesFetched() {
      this.$nextTick(() => {
        this.attributes = { data: Object.entries(this.customAtts) }
        this.$emit('data-change', _.cloneDeep(this.customAtts))
      })
    },
    checkAndFetchPreferences() {
      if (!this.subscriptionPreferencesEnabled) return
      this.$nextTick(() => {
        if (
          this.profileId &&
          Object.keys(this.customAtts || {}).some(attr =>
            this.isConsentPreference(attr)
          )
        ) {
          this.fetchSubscriptionPreferences(this.profileId)
        }
      })
    },
    setAttributeRules() {
      RestApi(
        projectEndpoint({
          method: 'get',
          url: `attributes`,
          params: {
            titles: Object.keys(this.customAtts),
            attribute_profile: 1
          }
        })
      ).then(({ data }) => {
        this.rules = {}
        var validator = (rule, value, callback) => {
          value = this.customAtts[rule.name]
          if (value === null) {
            callback()
            return
          }
          let valid = true
          switch (rule.type) {
            case 'boolean':
              if (!['1', '0', 1, 0].includes(value)) {
                valid = false
              }
              break
            case 'number':
              if (isNaN(parseInt(value)) || isNaN(value)) {
                valid = false
              }
              break
            case 'integer': {
              if (
                isNaN(parseInt(value)) ||
                isNaN(value) ||
                !_.isInteger(Number(value)) ||
                value.toString().includes('.')
              ) {
                valid = false
              }
              break
            }
          }

          if (!valid && value.toString().length !== 0) {
            callback(new Error(rule.name + ' is not a ' + rule.type))
          } else {
            callback()
          }
        }
        data.forEach(attr => {
          this.attrTypes[attr.name] = attr.type
          let type = null
          if (['number', 'unenforced_number'].includes(attr.type)) {
            type = 'number'
          }
          if (['integer', 'boolean'].includes(attr.type)) {
            type = attr.type
          }
          if (type) {
            this.rules[attr.name] = {
              name: attr.name,
              type: type,
              validator: validator,
              trigger: ['blur', 'change']
            }
          }
        })

        this.key = new Date().getTime()
      })
    },
    /**
     * Write an attribute value back to `customAtts`, then run the debounced
     * validate/emit. A plain assignment is used deliberately instead of `v-model`
     * (which compiles to `$set`): Vue 2's `$set` refuses to write keys that
     * exist on `Object.prototype` (e.g. `hasOwnProperty`, `toString`), silently
     * no-op'ing the update — see the `!(key in Object.prototype)` guard in
     * Vue's `set()`. The property already exists as a reactive accessor (seeded
     * via cloneDeep), so a direct assignment is reactive and works for any name.
     */
    onAttrInput(attrName, value, rules = null) {
      this.customAtts[attrName] = value

      clearTimeout(this.timeoutId)

      this.timeoutId = setTimeout(() => {
        this.parseBoolAttribute(attrName, rules)
        this.$refs['custom-data'].validate().then(() => {
          this.$emit('data-change', _.cloneDeep(this.customAtts))
        })
      }, 400)
    },
    parseBoolAttribute(attrName, rules) {
      if (rules && rules.type === 'boolean') {
        if (
          ['0', 'no', 'n', 'false', 'f'].includes(this.customAtts[attrName])
        ) {
          this.customAtts[attrName] = 0
        }
        if (
          ['1', 'yes', 'y', 'true', 't'].includes(this.customAtts[attrName])
        ) {
          this.customAtts[attrName] = 1
        }
      }
    },
    calculateHeight() {
      // Calculate dynamic height for attributes section
      let height = 0
      let minHeight = 250
      if (!document.querySelector('.sidebar-ct')) {
        this.height = '450px'
      }

      let sidebarHeight = document.querySelector('.sidebar-ct').clientHeight
      let optionsHeight = 0
      document.querySelectorAll('.campaign-preview-option').forEach(el => {
        optionsHeight += el.clientHeight || 0
      })
      height = sidebarHeight - optionsHeight - 165

      this.height = Math.max(height, minHeight) + 'px'
    },
    isUnmetAttributeConditionWithFallback(attribute) {
      const list =
        this.unmetConditions && this.unmetConditions['unmetConditionAttributes']
      return Array.isArray(list) && list.includes(attribute)
    },
    isUnmetAttributeConditionWithNoFallback(attribute) {
      const list =
        this.unmetConditions &&
        this.unmetConditions['unmetConditionAttributesNoFallback']
      return Array.isArray(list) && list.includes(attribute)
    },
    hasUnmetAttributeConditions(attribute) {
      return (
        this.isUnmetAttributeConditionWithFallback(attribute) ||
        this.isUnmetAttributeConditionWithNoFallback(attribute)
      )
    },
    getUnmetConditionsClass(attribute) {
      return { 'unmet-conditions': this.hasUnmetAttributeConditions(attribute) }
    },
    getUnmetTooltipMessage(attribute) {
      if (this.isUnmetAttributeConditionWithFallback(attribute)) {
        return this.$t(
          'CampaignForm_Preview_Attribute_Condition_Fallback_Applied'
        )
      }
      if (this.isUnmetAttributeConditionWithNoFallback(attribute)) {
        return this.$t(
          'CampaignForm_Preview_Attribute_Condition_No_Fallback_Available'
        )
      }
    },
    getConsentPreferenceValue(attribute, forSelect = false) {
      const getValue = value => {
        if (forSelect) {
          return value == null || value === 'NULL' ? 'NULL' : String(value)
        }
        return value || '-'
      }

      if (!this.isConsentPreference(attribute)) {
        return getValue(this.customAtts[attribute])
      }

      const parsed = this.parseConsentPreferenceKey(attribute)
      if (!parsed || parsed.channel == null || !parsed.name) {
        return getValue(this.customAtts[attribute])
      }

      const customAttrValue = this.customAtts[attribute]
      if (customAttrValue != null) {
        if (forSelect) {
          if (customAttrValue === 0 || customAttrValue === '0') return '0'
          if (customAttrValue === 1 || customAttrValue === '1') return '1'
          const numValue = Number(customAttrValue)
          if (!isNaN(numValue) && (numValue === 0 || numValue === 1)) {
            return String(numValue)
          }
        } else {
          return String(customAttrValue)
        }
      }

      const channelPrefs = this.subscriptionPreferences[parsed.channel]
      if (!channelPrefs) {
        return forSelect ? 'NULL' : '-'
      }

      const status = channelPrefs[parsed.name]
      if (status == null) {
        return 'NULL'
      }
      return String(status)
    },
    getConsentPreferenceValueForSelect(attribute) {
      return this.getConsentPreferenceValue(attribute, true)
    },
    onPreferenceChange(attribute, value) {
      const parsed = this.parseConsentPreferenceKey(attribute)
      const statusValue = value === 'NULL' ? null : Number(value)

      this.$set(this.customAtts, attribute, statusValue)

      if (parsed && parsed.channel !== undefined && parsed.name) {
        const channel = parsed.channel
        if (!this.subscriptionPreferences[channel]) {
          this.$set(this.subscriptionPreferences, channel, {})
        }
        this.$set(
          this.subscriptionPreferences[channel],
          parsed.name,
          statusValue
        )
      }

      this.$emit('data-change', _.cloneDeep(this.customAtts))
    }
  },
  destroyed() {
    EventBus.$off('set-profile-attributes', this.onSetProfileAttributes)
    EventBus.$off('set-unmet-attribute-conditions', this.onSetUnmetConditions)
    EventBus.$off('set-profile-id', this.onSetProfileId)
    EventBus.$off('reset-custom-data', this.onResetCustomData)
  }
}
</script>

<style scoped lang="scss">
.flexbox {
  display: flex;
  justify-content: space-between;
}

.flexbox:before,
.flexbox::after {
  display: inherit;
  content: none;
}

/deep/ .flexbox .el-form-item__label {
  font-size: 12px;
  font-weight: 600;
  padding-top: 4px;
}

/deep/ .borderless > .el-input__inner {
  border: 0;
  border-bottom: 1px solid #c2c7d3;
  border-radius: 0;
}

/deep/ .borderless-datepicker {
  .el-input__inner {
    border: 0;
    border-bottom: 1px solid #c2c7d3;
    border-radius: 0;
    padding: 0 15px;
  }

  .el-input__prefix {
    right: 5px;

    .el-icon-date,
    .el-icon-time {
      float: right;
    }
  }
}

/deep/ .unmet-conditions > .el-input__inner {
  background-color: #fde8e9;
}

.section-header {
  font-size: 14px;
  font-weight: 600;
  margin-top: 20px;
  margin-bottom: 10px;
  color: #303133;
}

.attributes-section:first-of-type .section-header {
  margin-top: 0;
}

.preferences-section {
  margin-top: 20px;
}

.attributes-section + .preferences-section {
  padding-top: 3px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: #e4e7ed;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }
}

/deep/ .preference-value {
  max-width: 70px;
}

/deep/ .preference-value > .el-input__inner {
  text-align: center;
}

/deep/ .preference-value.el-select {
  width: 70px;
}

/deep/ .preference-value.el-select .el-input__inner {
  border: 0;
  border-bottom: 1px solid #c2c7d3;
  border-radius: 0;
  text-align: center;
  padding: 0 20px 0 10px;
}

/deep/ .preference-value.unmet-conditions .el-input__inner {
  background-color: #fde8e9;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/preview/content-data-tabs/PreviewContentCustomDataTab.vue